import { useDeepEqualMemo } from '@superdispatch/hooks';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { kebabCase } from 'es-toolkit/compat';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import {
  APIPageQueryOptions,
  APIPageQueryResult,
  useAPIPageQuery,
} from 'shared/api/APIPageQuery';
import {
  APIQueryOptions,
  APIQueryResult,
  setAPIQueryData,
  useAPIQuery,
} from 'shared/api/APIQuery';
import {
  APIPageResponse,
  APIResponse,
  request,
  requestCarrierAPI,
  requestCarrierAPINoContent,
} from 'shared/api/CarrierAPIClient';
import { castAPIData } from 'shared/api/APISchema';
import { useFlag } from 'shared/settings/FeatureToggles';
import { shallowDiff } from 'shared/utils/DataUtils';
import {
  LoadBOLSendDTO,
  LoadCustomerEditDTO,
  LoadDeliveryEditDTO,
  LoadDetailsEditDTO,
  LoadPickupEditDTO,
  SendInvoiceEmailDTO,
  SendQuickBooksDTO,
} from './LoadActionsDTO';
import {
  AccountInfoDTO,
  AddLoadDTO,
  LoadCountsDTO,
  LoadDTO,
  LoadMarkAsPaidDTO,
  LoadPaymentDTO,
  loadSchema,
  LoadStage,
  LoadStagesCounts,
  LoadUpdateTerminalDTO,
  loadUpdateTerminalSchema,
  LOAD_STAGES,
  LoadSubhaulerPaymentDTO,
  CombinedLoadDTO,
  SubhauledLoadDTO,
  LoadPhotoSyncStatusDTO,
} from './LoadDTO';
import {
  LoadGroupByOptionsDTO,
  LoadGroupByParams,
  loadGroupByParamsSchema,
} from './LoadGroupByOptionsDTO';
import {
  DEFAULT_LOADS_PAGE_SIZE,
  getLoadsPageSize,
  LoadsPageParams,
} from './LoadsPageParams';
import {
  QuickBooksSettingsDTO,
  quickBooksSettingsSchema,
} from './QuickBooksSettingsDTO';
import {
  APIListQueryOptions,
  APIListQueryResult,
  useAPIListQuery,
} from 'shared/api/APIListQuery';
import { SubhaulResponseDTO } from 'carrier/vehicles/data/VehiclesDTO';
import { APIMutationOptions, useAPIMutation } from 'shared/api/APIMutation';
import { SUBHAULED_LIST_QUERY_KEY } from 'carrier/vehicles/data/VehiclesAPI';

export interface LoadsImportResult {
  loads: Array<{ guid: string; number: string }>;
  is_parsed_with_ai: boolean;
}

export interface DispatchSheetParseResult {
  loads: AddLoadDTO[];
  is_parsed_with_ai: boolean;
}

interface AIParsedLoadsResponse<TLoad> {
  loads: TLoad[];
  is_parsed_with_ai: boolean;
}

export class LoadsAPI {
  addLoad(load: AddLoadDTO): Promise<{ guid: string }> {
    return requestCarrierAPI<APIResponse<{ guid: string }>>(
      'POST /internal/web/loads/',
      {
        json: load,
      },
    ).then((response) => response.data);
  }

  uploadLoads(file: File): Promise<LoadsImportResult> {
    const body = new FormData();

    body.append('file', file);

    return requestCarrierAPI<
      APIResponse<LoadsImportResult['loads'] | LoadsImportResult>
    >('POST /internal/web/loads/import/', {
      body,
    }).then((response) => toAIParsedLoadsResponse(response.data));
  }

  parseDispatchSheet(file: File): Promise<DispatchSheetParseResult> {
    const body = new FormData();

    body.append('file', file);

    return requestCarrierAPI<
      APIResponse<AddLoadDTO[] | DispatchSheetParseResult>
    >('POST /internal/web/loads/parse-dispatch-sheet/', {
      body,
    }).then((response) => toAIParsedLoadsResponse(response.data));
  }

  duplicateLoad(guid: string): Promise<LoadDTO> {
    return requestCarrierAPI<APIResponse<LoadDTO>>(
      `POST /internal/web/loads/${guid}/duplicate/`,
    ).then((response) => response.data);
  }

  archiveLoad(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'PATCH /internal/web/loads/{guid}/archive/',
      { guid },
    ]);
  }

  unarchiveLoad(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'PATCH /internal/web/loads/{guid}/unarchive/',
      { guid },
    ]);
  }

  deleteLoad(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'DELETE /internal/web/loads/{guid}/delete/',
      { guid },
    ]);
  }

  restoreLoad(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'PATCH /internal/web/loads/{guid}/restore/',
      { guid },
    ]);
  }

  assignDriver(guid: string, driverGUID: string): Promise<APIResponse> {
    return requestCarrierAPI(
      ['POST /internal/web/loads/{guid}/assign/', { guid }],
      {
        json: { driver_guid: driverGUID },
      },
    );
  }

  reassignDriver(guid: string, driverGUID: string): Promise<APIResponse> {
    return this.unassignDriver(guid).then(() =>
      this.assignDriver(guid, driverGUID),
    );
  }

  unassignDriver(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'POST /internal/web/loads/{guid}/unassign/',
      { guid },
    ]);
  }

  markAsNew(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'POST /internal/web/loads/{guid}/mark-as-new/',
      { guid },
    ]);
  }

  markAsPickedUp(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'POST /internal/web/loads/{guid}/mark-as-picked-up/',
      { guid },
    ]);
  }

  markAsDelivered(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'POST /internal/web/loads/{guid}/mark-as-delivered/',
      { guid },
    ]);
  }

  markAsPaid(guid: string, values: LoadMarkAsPaidDTO): Promise<APIResponse> {
    return requestCarrierAPI(
      ['POST /internal/web/loads/{guid}/mark-as-paid/', { guid }],
      {
        json: values,
      },
    );
  }

  markAsUnpaid(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'POST /internal/web/loads/{guid}/mark-as-unpaid/',
      { guid },
    ]);
  }

  markAsPaidToSubhauler(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'POST /internal/web/loads/subhauled/{guid}/subhauler/mark-as-paid/',
      { guid },
    ]);
  }

  markAsUnpaidToSubhauler(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'POST /internal/web/loads/subhauled/{guid}/subhauler/mark-as-unpaid/',
      { guid },
    ]);
  }

  addTerminal(
    guid: string,
    terminalGUID: string,
  ): Promise<APIResponse<LoadUpdateTerminalDTO>> {
    return requestCarrierAPI<APIResponse>(
      ['PATCH /internal/web/loads/{guid}/add-terminal/', { guid }],
      { json: { terminal_guid: terminalGUID } },
    ).then((response) => ({
      ...response,
      data: castAPIData(loadUpdateTerminalSchema, response.data),
    }));
  }

  removeLastTerminal(
    guid: string,
  ): Promise<APIResponse<LoadUpdateTerminalDTO>> {
    return requestCarrierAPI<APIResponse>([
      'PATCH /internal/web/loads/{guid}/remove-terminal/',
      { guid },
    ]).then((response) => ({
      ...response,
      data: castAPIData(loadUpdateTerminalSchema, response.data),
    }));
  }

  markAsInTerminal(guid: string, terminalGUID: string): Promise<APIResponse> {
    return requestCarrierAPI(
      ['PATCH /internal/web/loads/{guid}/mark-as-in-terminal/', { guid }],
      {
        json: { terminal_guid: terminalGUID },
      },
    );
  }

  dispatchToCarrier(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'POST /internal/web/loads/{guid}/dispatch-to-carrier/',
      { guid },
    ]);
  }

  removeDispatchedToCarrierLabel(guid: string): Promise<APIResponse> {
    return requestCarrierAPI([
      'POST /internal/web/loads/{guid}/remove-dispatched-to-carrier-label/',
      { guid },
    ]);
  }

  getQuickBooksSettings(): Promise<QuickBooksSettingsDTO> {
    return requestCarrierAPI(
      'GET /quickbooks/desktop/carrier-connection-info/',
    ).then(({ data }) => castAPIData(quickBooksSettingsSchema, data));
  }

  getLoadInvoiceInfo(guid: string) {
    return requestCarrierAPI<
      APIResponse<{ qb_desktop: { connected: boolean } }>
    >(['GET /accounting/invoice-info/{?order_guid}', { order_guid: guid }]);
  }

  getBolPDF(guid: string) {
    return request(['GET /internal/web/loads/{guid}/bol-pdf/', { guid }]);
  }

  getSubhaulerBolPDF(guid: string) {
    return request([
      'GET /internal/web/loads/subhauled/{guid}/bol/pdf/',
      { guid },
    ]);
  }

  getInvoicePDF(guid: string) {
    return request(['GET /internal/web/loads/{guid}/invoice-pdf/', { guid }]);
  }

  moveToVehicles(guid: string) {
    return requestCarrierAPINoContent([
      'POST /internal/web/loads/{guid}/move-to-vehicles/',
      { guid },
    ]);
  }
}

export function useLoadsAPI() {
  const { invalidateLoads } = useLoadsCache();

  return useMemo(() => {
    function subhaulLoad(guid: string, json: SubhauledLoadDTO) {
      return requestCarrierAPI<APIResponse<SubhaulResponseDTO[]>>(
        ['POST /internal/web/loads/{guid}/subhaul/', { guid }],
        {
          json,
        },
      ).then((response) => {
        invalidateLoads();
        return response;
      });
    }

    function patchLoadDetails(
      guid: string,
      updated: LoadDetailsEditDTO,
      original?: LoadDetailsEditDTO,
    ) {
      return requestCarrierAPI(
        ['PATCH /internal/web/loads/{guid}/edit/', { guid }],
        {
          json: shallowDiff(updated, original),
        },
      );
    }

    function editLoadPickup(guid: string, json: LoadPickupEditDTO) {
      return requestCarrierAPI(
        ['POST /internal/web/loads/{guid}/edit-pickup-info/', { guid }],
        {
          json,
        },
      );
    }

    function editLoadDelivery(guid: string, json: LoadDeliveryEditDTO) {
      return requestCarrierAPI(
        ['POST /internal/web/loads/{guid}/edit-delivery-info/', { guid }],
        {
          json,
        },
      );
    }

    function editLoadCustomer(guid: string, json: LoadCustomerEditDTO) {
      return requestCarrierAPI(
        ['POST /internal/web/loads/{guid}/edit-customer-info/', { guid }],
        {
          json,
        },
      );
    }

    function editLoadPayment(guid: string, json: Partial<LoadPaymentDTO>) {
      return requestCarrierAPI(
        ['POST /internal/web/loads/{guid}/edit-payment-info/', { guid }],
        {
          json,
        },
      );
    }

    function editLoadSubhaulerPayment(
      guid: string,
      json: LoadSubhaulerPaymentDTO,
    ) {
      return requestCarrierAPI(
        [
          'PATCH /internal/web/loads/subhauled/{guid}/edit-payment-agreement/',
          { guid },
        ],
        {
          json,
        },
      );
    }

    function addLoadNote(guid: string, text: string) {
      return requestCarrierAPI(
        ['POST /internal/web/loads/{guid}/add-note/', { guid }],
        {
          json: { text },
        },
      );
    }

    function deleteLoadNote(guid: string, noteId: number) {
      return requestCarrierAPI([
        'DELETE /internal/web/loads/{guid}/notes/{note_id}/',
        { guid, note_id: noteId },
      ]);
    }

    function sendBOL(guid: string, values: LoadBOLSendDTO) {
      return requestCarrierAPI(
        ['POST /internal/web/bol/{guid}/send/', { guid }],
        { json: values },
      );
    }

    function sendSubhaulerBOL(guid: string, values: LoadBOLSendDTO) {
      return requestCarrierAPI(
        ['POST /internal/web/loads/subhauled/{guid}/bol/send/', { guid }],
        { json: values },
      );
    }

    function cancelLoad(
      guid: string,
      values: { cancel_reason_type: string; cancel_reason: string },
    ) {
      return requestCarrierAPI(
        ['POST /internal/web/loads/{guid}/cancel/', { guid }],
        {
          json: values,
        },
      );
    }

    function updateLoadAttachmentVisibility(
      attachmentGUID: string,
      isVisibleForDriver: boolean,
    ) {
      return requestCarrierAPI(
        [
          'PATCH /internal/web/attachments/{attachmentGUID}/',
          { attachmentGUID },
        ],
        { json: { is_visible_for_driver: isVisibleForDriver } },
      ).then((response) => {
        invalidateLoads();
        return response;
      });
    }

    function revokeLoad(guid?: string) {
      return requestCarrierAPI([
        'POST /internal/web/loads/subhauled/{guid}/revoke/',
        { guid },
      ]);
    }

    return {
      revokeLoad,
      subhaulLoad,
      patchLoadDetails,
      editLoadPickup,
      editLoadDelivery,
      editLoadCustomer,
      editLoadPayment,
      editLoadSubhaulerPayment,
      addLoadNote,
      deleteLoadNote,
      sendBOL,
      sendSubhaulerBOL,
      cancelLoad,
      updateLoadAttachmentVisibility,
    };
  }, [invalidateLoads]);
}

export interface LoadInvoicePreviewParams {
  load_guids: string;
  invoice_number: string;
  invoice_date: string;
  bol_template: string;
  factor_this_invoice: boolean;
  is_carrier_requested_superpay: boolean | undefined;
}

export function useGetCombinedInvoicePDF(
  params: LoadInvoicePreviewParams,
  options?: APIQueryOptions<{ html: string }>,
): APIQueryResult<{ html: string }> {
  return useAPIQuery(
    ['get_combined_invoice_pdf', 'invoice', { params }],
    () =>
      requestCarrierAPI([
        'GET /internal/web/loads/preview-invoice/{?params*}',
        { params },
      ]),
    options,
  );
}

export function useSendInvoiceEmail(
  invoiceEmail: SendInvoiceEmailDTO,
  options?: APIQueryOptions<SendInvoiceEmailDTO>,
) {
  return useAPIQuery(
    ['send_invoice_email', 'invoice'],
    () =>
      requestCarrierAPI('POST /internal/web/loads/send-invoice-email/', {
        json: invoiceEmail,
      }),
    {
      ...options,
      //there do need to keep data on cache, because while open/close drawer multiple times it should refetch data and isLoading status need to work
      gcTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    },
  );
}

export function useQbInvoice(
  type: 'online' | 'desktop',
  quickBook: SendQuickBooksDTO,
  options?: APIQueryOptions<SendQuickBooksDTO>,
) {
  return useAPIQuery(
    [`send_invoice_quickbook_${type}`, 'quickbook'],
    () =>
      requestCarrierAPI(`POST /quickbooks/${type}/send-combined-invoice/`, {
        json: quickBook,
      }),
    {
      ...options,
      //there do need to keep data on cache, because while open/close drawer multiple times it should refetch data and isLoading status need to work
      gcTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    },
  );
}

export function useLoadsPage(
  params: LoadsPageParams,
  options?: APIPageQueryOptions<LoadDTO>,
): APIPageQueryResult<LoadDTO> {
  return useAPIPageQuery(
    ['loads', 'page', params],
    () => {
      const { stage, page_size, ...rest } = normalizeParams(params);
      const pageSize = getLoadsPageSize(params);
      // The default size is left out, so the request stays as it was
      const query =
        pageSize === DEFAULT_LOADS_PAGE_SIZE
          ? rest
          : { ...rest, page_size: pageSize };

      if (stage === 'receivable/past-due') {
        return requestCarrierAPI([
          'GET /internal/web/loads/receivable/past-due/{?query*}',
          { query },
        ]);
      }

      return requestCarrierAPI([
        'GET /internal/web/loads/{stage}/{?query*}',
        { query, stage: kebabCase(stage) },
      ]);
    },
    {
      keepPreviousData: params.stage !== 'active',
      schema: loadSchema,
      ...options,
    },
  );
}

function normalizeParams(params: LoadsPageParams) {
  const {
    number,
    vehicle,
    vin,
    order_id,
    customer,
    pickup_name,
    delivery_name,
    internal_load_id,
    invoice_id,
    all,
    stage,
  } = params;

  // if stage is active, there have to be send with one of the criteria, otherwise it will return all loads
  if (
    !(
      number ||
      vehicle ||
      vin ||
      order_id ||
      customer ||
      pickup_name ||
      delivery_name ||
      internal_load_id ||
      invoice_id ||
      all
    ) &&
    stage === 'active'
  ) {
    return { ...params, stage: 'new' };
  }

  return params;
}

interface LoadCountsBroadcastMessage {
  type: 'load-counts-sync';
  source: string;
  paramsKey: string;
  counts: Array<[LoadStage, LoadCountsDTO]>;
}

const LOAD_COUNTS_BROADCAST_CHANNEL = `carrier-tms-load-counts-${
  import.meta.env.VITE_APP_NAME || 'app'
}`;

function getLoadCountsBroadcastSource() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36);
}

const LOAD_COUNTS_BROADCAST_SOURCE = getLoadCountsBroadcastSource();

function useLoadCountsBroadcast(
  params: LoadsPageParams,
  counts: Map<LoadStage, LoadCountsDTO> | undefined,
) {
  const queryClient = useQueryClient();
  const channelRef = useRef<BroadcastChannel | null>(null);
  const skipNextBroadcastRef = useRef(false);
  // The page size does not change the counts, so tabs with different sizes still sync
  const { stage, page, page_size, ...paramsAffectingCounts } = params;
  const paramsKey = JSON.stringify(paramsAffectingCounts);

  useEffect(() => {
    if (import.meta.env.MODE === 'test') return;
    if (params.stage === 'active') return;
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
      return;
    }

    const channel = new BroadcastChannel(LOAD_COUNTS_BROADCAST_CHANNEL);
    channelRef.current = channel;

    function onMessage(event: MessageEvent<LoadCountsBroadcastMessage>) {
      const message = event.data;
      if (!message || message.type !== 'load-counts-sync') return;
      if (message.source === LOAD_COUNTS_BROADCAST_SOURCE) return;
      if (message.paramsKey !== paramsKey) return;

      // Must match the `useLoadCounts` query key, which leaves out `page_size`
      const { page_size: _pageSize, ...countParams } = params;

      skipNextBroadcastRef.current = true;
      queryClient.setQueryData(
        ['loads', 'count', { params: countParams }],
        new Map<LoadStage, LoadCountsDTO>(message.counts),
      );
    }

    channel.addEventListener('message', onMessage);

    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
      channelRef.current = null;
    };
  }, [params, paramsKey, queryClient]);

  useEffect(() => {
    if (!counts) return;
    if (params.stage === 'active') return;

    if (skipNextBroadcastRef.current) {
      skipNextBroadcastRef.current = false;
      return;
    }

    channelRef.current?.postMessage({
      type: 'load-counts-sync',
      source: LOAD_COUNTS_BROADCAST_SOURCE,
      paramsKey,
      counts: Array.from(counts.entries()),
    } satisfies LoadCountsBroadcastMessage);
  }, [counts, params.stage, paramsKey]);
}

export function useLoadCounts(
  params: LoadsPageParams,
  options?: Pick<APIQueryOptions<Map<LoadStage, LoadCountsDTO>>, 'enabled'>,
): APIQueryResult<Map<LoadStage, LoadCountsDTO>> {
  // The page size does not change the counts
  const { page_size, ...countParams } = params;

  const result = useAPIQuery(
    ['loads', 'count', { params: countParams }],
    () => {
      const { page, stage, order_by, ...query } = countParams;

      return requestCarrierAPI([
        'GET /internal/web/loads/count/{?query*}',
        { query },
      ]);
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: params.stage !== 'active' && (options?.enabled ?? true),
      normalize(response: LoadStagesCounts) {
        const counts = new Map<LoadStage, LoadCountsDTO>();

        for (const stage of LOAD_STAGES) {
          const value = response[stage];

          if (value != null) {
            counts.set(stage, value);
          }
        }

        return counts;
      },
    },
  ) as APIQueryResult<Map<LoadStage, LoadCountsDTO>>;

  useLoadCountsBroadcast(params, result.data);

  return result;
}

/**
 * Total load count for list pagination. When `ctms_loads_page_count` is enabled, uses the
 * same source as tab badges (`useLoadCounts` / GET /loads/count/).
 *
 * `stage === 'active'` (global search) is out of scope: `useLoadCounts` never runs for
 * that stage, so with the flag on we still use `paginationCount` from the loads list
 * response and do not call the count API.
 */
export function useLoadsPageCount(
  params: LoadsPageParams,
  paginationCount: number | undefined,
): number | undefined {
  const shouldUseLoadCounts = useFlag('ctms_loads_page_count');
  const { data: loadCounts } = useLoadCounts(params, {
    enabled: shouldUseLoadCounts,
  });

  if (!shouldUseLoadCounts) {
    return paginationCount;
  }

  return loadCounts?.get(params.stage)?.count ?? paginationCount;
}

export const LoadsInvalidationContext = createContext<undefined | (() => void)>(
  undefined,
);

export function useLoadsCache() {
  const queryClient = useQueryClient();
  const onInvalidate = useContext(LoadsInvalidationContext);

  return useMemo(() => {
    function invalidateLoads() {
      onInvalidate?.();
      void queryClient.invalidateQueries({ queryKey: ['loads'] });
    }

    function setLoadsPageItems(
      params: LoadsPageParams,
      fn: (items: LoadDTO[]) => LoadDTO[],
    ) {
      setAPIQueryData<APIPageResponse<LoadDTO>>(
        queryClient,
        ['loads', 'page', params],
        (response) => ({ ...response, data: fn(response.data) }),
      );
      invalidateLoads();
    }

    return { invalidateLoads, setLoadsPageItems };
  }, [queryClient, onInvalidate]);
}

export function useLoadsGroup(
  params: LoadGroupByParams,
): APIQueryResult<LoadGroupByOptionsDTO> {
  const query = useDeepEqualMemo(
    () => castAPIData(loadGroupByParamsSchema, params),
    [params],
  );

  return useAPIQuery(['loads', 'groupBy', { query }], () =>
    requestCarrierAPI(['GET /internal/web/loads/groupby/{?query*}', { query }]),
  );
}

function useSubhauledLoadCache(guid: null | string | undefined) {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: ['loads', 'item-subhauled', { guid }],
    });
    queryClient.invalidateQueries({
      queryKey: SUBHAULED_LIST_QUERY_KEY,
    });
  };
}

export function getLoadDetailsQueryKey(
  guid: null | string | undefined,
  isSubhauled: boolean | undefined,
) {
  return [
    'loads',
    `item-${isSubhauled ? 'subhauled' : 'regular'}`,
    { guid },
  ] as const;
}

export function useIsLoadDetailsRefetchingAfterUpdate(
  guid: null | string | undefined,
  { isSubhauled }: { isSubhauled?: boolean } = {},
): boolean {
  const queryClient = useQueryClient();
  const queryKey = getLoadDetailsQueryKey(guid, isSubhauled);
  const isFetching = useIsFetching({ queryKey, exact: true }) > 0;

  return isFetching && !!queryClient.getQueryState(queryKey)?.isInvalidated;
}

export function useLoadDetails(
  guid: null | string | undefined,
  { isSubhauled }: { isSubhauled?: boolean } = {},
  options?: APIQueryOptions<LoadDTO>,
): APIQueryResult<LoadDTO> {
  return useAPIQuery(
    getLoadDetailsQueryKey(guid, isSubhauled),
    () =>
      requestCarrierAPI([
        isSubhauled
          ? 'GET /internal/web/loads/subhauled/{guid}/'
          : 'GET /internal/web/loads/{guid}/',
        { guid },
      ]),
    { enabled: !!guid, ...options },
  );
}

interface LoadBOLPreview {
  html: string;
}

export interface LoadBOLPReviewParams {
  template?: null | string;
  use_broker_info?: null | boolean;
}

export function useLoadBOLPreview(
  guid: null | string | undefined,
  params?: LoadBOLPReviewParams,
  isSubhauled?: boolean,
): APIQueryResult<LoadBOLPreview> {
  return useAPIQuery(
    ['loads', 'bolPreview', { guid, params }],
    () =>
      requestCarrierAPI([
        isSubhauled
          ? 'GET /internal/web/loads/subhauled/{guid}/bol/preview/{?params*}'
          : 'GET /internal/web/bol/{guid}/preview/{?params*}',
        { guid, params },
      ]),
    { enabled: !!guid },
  );
}

export function useRelatedLoads(guid: string): APIQueryResult<
  Array<{
    guid: string;
    number: string;
    status: string;
    is_in_terminal: boolean;
  }>
> {
  return useAPIQuery(
    ['loads-related', guid],
    () => {
      return requestCarrierAPI([
        'GET /internal/web/loads/{guid}/loads-to-cancel/',
        { guid },
      ]);
    },
    { enabled: !!guid, refetchOnWindowFocus: false },
  );
}

interface AccountInfoParams {
  order_guid?: string;
  skip_customer_name?: boolean;
}

export function useAccountingInfo(
  params?: AccountInfoParams,
  options?: APIQueryOptions<AccountInfoDTO>,
): APIQueryResult<AccountInfoDTO> {
  return useAPIQuery(
    ['account_info', 'item', { params }],
    () =>
      requestCarrierAPI([
        'GET /accounting/invoice-info/{?page,params*}',
        { params },
      ]),
    {
      ...options,
      enabled: !!params?.order_guid,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  );
}

interface OrdersPageParams {
  q: string;
  page_size: number;
  customer_name: string;
}

export function useCombinedLoads(
  guid?: string,
  params?: OrdersPageParams,
  options?: APIListQueryOptions<CombinedLoadDTO>,
): APIListQueryResult<CombinedLoadDTO> {
  return useAPIListQuery(
    ['order_list', 'list', { params, guid }],
    (page) =>
      requestCarrierAPI([
        'GET /internal/web/accounting/combined-invoice/loads/{?page,params*}',
        { page, params },
      ]),
    {
      ...options,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  );
}

export function useRevokeSubhauledLoad(
  loadGUID: string | undefined,
  options?: APIMutationOptions<void>,
) {
  const invalidateLoad = useSubhauledLoadCache(loadGUID);
  const { revokeLoad } = useLoadsAPI();
  return useAPIMutation(
    () =>
      revokeLoad(loadGUID).then((response) => {
        invalidateLoad();
        return response;
      }),
    options,
  );
}

export function useLoadPhotoSyncStatus(
  guid: string,
  options?: APIQueryOptions<LoadPhotoSyncStatusDTO>,
): APIQueryResult<LoadPhotoSyncStatusDTO> {
  return useAPIQuery(
    ['loads', 'photo-sync-status', { guid }],
    () =>
      requestCarrierAPI([
        'GET /internal/web/loads/{guid}/photo-sync-status/',
        { guid },
      ]),
    { ...options },
  );
}

// The API wraps the loads in an object only when ctms_ai_document_parser_fallback is on for the carrier.
function toAIParsedLoadsResponse<TLoad>(
  data: TLoad[] | AIParsedLoadsResponse<TLoad>,
): AIParsedLoadsResponse<TLoad> {
  if (Array.isArray(data)) {
    return { loads: data, is_parsed_with_ai: false };
  }

  return data;
}
